import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, ArrowDownRight, ArrowUpRight, Clock, Search, Inbox, Eye } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useAgents } from '@/hooks/useAgents'
import type { AgentSummary } from '@/lib/api'
import { getAgentIcon } from '@/components/agents/AgentIcons'
import { AgentInspectorPanel, type InspectorTarget } from '@/components/agents/AgentInspectorPanel'
import {
  DIRECTORY_SECTIONS,
  ALL_DIRECTORY_AGENTS,
  repoLabel,
  type DirectoryAgent,
  type AgentRepo,
} from '@/lib/agent-directory'
import { getPhaseLabel, type AgentPhase } from '@/lib/agent-org'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return 'NEVER'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'JUST NOW'
  if (mins < 60) return `${mins}M AGO`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}H AGO`
  return `${Math.floor(hours / 24)}D AGO`
}

type LiveState = 'active' | 'paused' | 'error' | 'offline'

function liveStateOf(live: AgentSummary | undefined): LiveState {
  if (!live) return 'offline'
  switch (live.status) {
    case 'active': return 'active'
    case 'paused': return 'paused'
    case 'error':  return 'error'
    default:       return 'offline'
  }
}

function StatusBadge({ live, phase }: { live: AgentSummary | undefined; phase: AgentPhase }) {
  if (!live) {
    const label = phase === 'live' ? 'NO TELEMETRY' : getPhaseLabel(phase)
    return <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-mono shrink-0">{label}</Badge>
  }
  const state = liveStateOf(live)
  const variant = state === 'active' ? 'success' : state === 'error' ? 'destructive' : state === 'paused' ? 'warning' : 'outline'
  return (
    <Badge variant={variant} className="text-[9px] px-1.5 py-0 font-mono shrink-0">
      {state.toUpperCase()}
    </Badge>
  )
}

function RepoBadge({ repo }: { repo: AgentRepo }) {
  return (
    <span
      className={`font-mono text-[9px] px-1.5 py-0.5 uppercase tracking-wider ${
        repo === 'runtime' ? 'bg-[#1a2029] text-primary' : 'bg-[#1a2029] text-[#22d3ee]'
      }`}
    >
      {repoLabel(repo)}
    </span>
  )
}

// ─── Detail list (consumes / feeds) ───────────────────────────────────────────

function FlowList({
  title,
  icon,
  items,
  accent,
}: {
  title: string
  icon: React.ReactNode
  items: string[]
  accent: string
}) {
  return (
    <div>
      <p className="flex items-center gap-1.5 font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
        <span style={{ color: accent }}>{icon}</span>
        {title}
      </p>
      {items.length === 0 ? (
        <p className="mt-1.5 font-mono text-[0.65rem] text-cmd-outline-variant">—</p>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {items.map((it) => (
            <li key={it} className="font-mono text-[0.7rem] text-white/80 leading-snug">
              {it}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Expandable row ────────────────────────────────────────────────────────────

function DirectoryRow({
  agent,
  live,
  expanded,
  onToggle,
  onInspect,
}: {
  agent: DirectoryAgent
  live: AgentSummary | undefined
  expanded: boolean
  onToggle: () => void
  onInspect: () => void
}) {
  const Icon = getAgentIcon(agent.skillName) ?? getAgentIcon(agent.displayName)
  const dim = agent.phase !== 'live' && agent.phase !== 'infrastructure'
  const panelId = `agent-detail-${agent.skillName}`

  return (
    <div className={`relative bg-cmd-container ${dim ? 'opacity-70' : ''}`}>
      <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ backgroundColor: agent.accent }} />

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 p-3 pl-4 text-left transition-colors hover:bg-cmd-high focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-cmd-outline transition-transform ${expanded ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        <div className="flex h-8 w-8 shrink-0 items-center justify-center bg-cmd-high text-sm">
          {Icon ? <Icon size={28} /> : <span>{agent.emoji}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-sans text-sm font-semibold text-white truncate">{agent.displayName}</h3>
            <span className="font-label text-[0.6rem] text-cmd-outline truncate hidden sm:inline">
              {agent.role}
            </span>
          </div>
          <p className="font-label text-[0.6rem] text-cmd-outline-variant truncate italic">
            {agent.identity}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <RepoBadge repo={agent.repo} />
          <span className="font-mono text-[0.6rem] text-cmd-outline">{agent.model}</span>
        </div>
        <StatusBadge live={live} phase={agent.phase} />
      </button>

      {expanded && (
        <div id={panelId} className="border-t border-cmd-high px-4 py-4 pl-9 space-y-4 animate-fade-in-up">
          {/* Meta row — shows on small screens where the collapsed row hides it */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 md:hidden">
              <RepoBadge repo={agent.repo} />
              <span className="font-mono text-[0.6rem] text-cmd-outline">{agent.model}</span>
            </div>
            {/* Inspector is runtime-only — Council/infra agents live in this
                repo's skill loader + memory store. Product agents would 404. */}
            {agent.repo === 'runtime' && (
              <button
                type="button"
                onClick={onInspect}
                className="ml-auto flex items-center gap-1.5 bg-cmd-high px-2.5 py-1.5 font-label text-[0.65rem] tracking-wider uppercase text-primary transition-colors hover:bg-cmd-highest focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                Inspect
              </button>
            )}
          </div>

          {/* Identity + reporting line */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Meta label="Role" value={agent.role} />
            <Meta label="Reports to" value={agent.reportsTo} />
            <Meta label="Tier" value={agent.tier.replace('-', ' ').toUpperCase()} />
          </div>

          {/* Schedule */}
          <div>
            <p className="flex items-center gap-1.5 font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
              <Clock className="h-3 w-3" style={{ color: agent.accent }} aria-hidden="true" />
              Schedule
            </p>
            {agent.cron.length === 0 ? (
              <p className="mt-1.5 font-mono text-[0.7rem] text-cmd-outline-variant">Event-driven · no cron</p>
            ) : (
              <ul className="mt-1.5 space-y-1">
                {agent.cron.map((c) => (
                  <li key={c} className="font-mono text-[0.7rem] text-white/80">{c}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Data flow */}
          <div className="grid gap-4 sm:grid-cols-2">
            <FlowList
              title="Consumes from"
              icon={<ArrowDownRight className="h-3 w-3" />}
              items={agent.consumesFrom}
              accent={agent.accent}
            />
            <FlowList
              title="Feeds into"
              icon={<ArrowUpRight className="h-3 w-3" />}
              items={agent.feedsInto}
              accent={agent.accent}
            />
          </div>

          {/* Live telemetry */}
          {live ? (
            <div className="border-t border-cmd-high pt-3 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Meta label="Runs today" value={String(live.runs_today)} mono />
                <Meta label="Runs 7d" value={String(live.runs_7d)} mono />
                <Meta
                  label="Last run"
                  value={`${timeAgo(live.last_run_at)}${live.last_run_outcome ? ` · ${live.last_run_outcome.toUpperCase()}` : ''}`}
                  mono
                />
                <Meta
                  label="Success 24h"
                  value={live.success_rate_24h !== null ? `${live.success_rate_24h}%` : '--'}
                  mono
                />
                <Meta
                  label="Success 7d"
                  value={live.success_rate_7d !== null ? `${live.success_rate_7d}%` : '--'}
                  mono
                />
                <Meta label="Next run" value={live.schedule_summary ?? 'Event-driven'} mono />
              </div>
              {live.pending_decisions > 0 && (
                <Link
                  to={`/dashboard/decisions?agent=${encodeURIComponent(live.name)}`}
                  className="flex items-center gap-2 bg-[#3d2e00] px-3 py-2 text-[0.75rem] text-[#ffd080] hover:bg-[#4a3800] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                >
                  <Inbox className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="flex-1">
                    {live.pending_decisions} decision{live.pending_decisions === 1 ? '' : 's'} waiting for your sign-off
                  </span>
                  <span className="font-label text-[0.6rem] tracking-wider">REVIEW →</span>
                </Link>
              )}
            </div>
          ) : (
            <p className="border-t border-cmd-high pt-3 font-mono text-[0.65rem] text-cmd-outline-variant">
              {agent.phase === 'live'
                ? 'No runtime telemetry yet — agent has not reported a run.'
                : 'Specced — not yet built.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">{label}</p>
      <p className={`mt-0.5 text-xs text-white/90 ${mono ? 'font-mono tabular-nums' : 'font-label'}`}>{value}</p>
    </div>
  )
}

// ─── Section ───────────────────────────────────────────────────────────────────

function SectionHeader({ label, blurb, count }: { label: string; blurb: string; count: number }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <div>
        <h2 className="font-sans text-[0.7rem] font-semibold tracking-widest text-white uppercase">{label}</h2>
        <p className="font-label text-[0.6rem] text-cmd-outline">{blurb}</p>
      </div>
      <span className="font-mono text-[0.6rem] text-cmd-outline-variant tabular-nums">{count}</span>
    </div>
  )
}

// ─── Page-level directory ────────────────────────────────────────────────────

type RepoFilter = 'ALL' | 'RUNTIME' | 'PRODUCT'

export function AgentDirectory() {
  const { data, isLoading, error } = useAgents()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [inspecting, setInspecting] = useState<InspectorTarget | null>(null)
  const [query, setQuery] = useState('')
  const [repoFilter, setRepoFilter] = useState<RepoFilter>('ALL')

  const liveByName = useMemo(() => {
    const map = new Map<string, AgentSummary>()
    for (const a of data?.data ?? []) map.set(a.name, a)
    if (!map.has('guardian-coo') && map.has('chief-of-staff')) {
      map.set('guardian-coo', map.get('chief-of-staff')!)
    }
    return map
  }, [data])

  const liveCount = useMemo(
    () => ALL_DIRECTORY_AGENTS.filter((a) => liveStateOf(liveByName.get(a.skillName)) === 'active').length,
    [liveByName],
  )

  const matches = (a: DirectoryAgent): boolean => {
    if (repoFilter === 'RUNTIME' && a.repo !== 'runtime') return false
    if (repoFilter === 'PRODUCT' && a.repo !== 'product') return false
    if (!query.trim()) return true
    const q = query.toLowerCase()
    return (
      a.displayName.toLowerCase().includes(q) ||
      a.role.toLowerCase().includes(q) ||
      a.skillName.toLowerCase().includes(q) ||
      a.identity.toLowerCase().includes(q)
    )
  }

  const sections = DIRECTORY_SECTIONS.map((s) => ({
    ...s,
    agents: s.agents.filter(matches),
  })).filter((s) => s.agents.length > 0)

  const totalShown = sections.reduce((n, s) => n + s.agents.length, 0)

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-cmd-outline" aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agents…"
            aria-label="Search agents by name, role, or identity"
            className="w-full bg-cmd-container py-2 pl-8 pr-3 font-label text-xs text-white placeholder:text-cmd-outline-variant focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-cmd-container p-1">
            {(['ALL', 'RUNTIME', 'PRODUCT'] as RepoFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setRepoFilter(f)}
                className={`px-2.5 py-1 font-label text-[0.65rem] tracking-wider transition-colors ${
                  repoFilter === f ? 'bg-cmd-high text-primary' : 'text-cmd-outline hover:text-white'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <span className="font-mono text-[0.65rem] text-cmd-outline tabular-nums whitespace-nowrap">
            {liveCount} ACTIVE · {ALL_DIRECTORY_AGENTS.length} TOTAL
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-cmd-error-container p-4">
          <p className="font-label text-sm text-cmd-on-error">
            FAULT DETECTED: {error.message} — live status unavailable; roster still shown below.
          </p>
        </div>
      )}

      {/* Sections */}
      {isLoading ? (
        <div className="space-y-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : totalShown === 0 ? (
        <div className="bg-cmd-container p-8 text-center">
          <p className="font-label text-sm text-cmd-outline">NO AGENTS MATCH YOUR FILTER</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.tier}>
              <SectionHeader label={section.label} blurb={section.blurb} count={section.agents.length} />
              <div className="space-y-px">
                {section.agents.map((agent) => (
                  <DirectoryRow
                    key={agent.skillName}
                    agent={agent}
                    live={liveByName.get(agent.skillName)}
                    expanded={expanded === agent.skillName}
                    onToggle={() => setExpanded(expanded === agent.skillName ? null : agent.skillName)}
                    onInspect={() =>
                      setInspecting({
                        skillName: agent.skillName,
                        displayName: agent.displayName,
                        emoji: agent.emoji,
                        role: agent.role,
                        accent: agent.accent,
                      })
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AgentInspectorPanel
        target={inspecting}
        live={inspecting ? liveByName.get(inspecting.skillName) : undefined}
        onClose={() => setInspecting(null)}
      />
    </div>
  )
}
